import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { orange } from "@mui/material/colors";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import CountryFlag from "@entrilia/shared/components/CountryFlag";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { api } from "../../../api/operationsApi";
import type { ClientCodeFromWebsiteUrlAccessors } from "../../../hooks/useClientCodeFromWebsiteUrl";
import { useClientCodeFromWebsiteUrl } from "../../../hooks/useClientCodeFromWebsiteUrl";
import { useExecutionState } from "../../../hooks/useExecutionState";
import { getVariables } from "../../../variables";
import { isFormValid } from "../../../utilities/forms";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";
import { doesClientCodeExist, getClientCodeHelperText } from "./clientManagementFormHelpers";
import type { ClientLocation, FundAdminProvisioningForm } from "./fundAdminProvisioningForm";
import {
  getInitialFundAdminProvisioningForm,
  mapFundAdminProvisioningFormToRequest,
  validateFundAdminProvisioningForm,
} from "./fundAdminProvisioningForm";

const provisionFundAdminClient = withErrorHandling(api.clientProvisioning.provisionFundAdminClient);

const clientCodeAccessors: ClientCodeFromWebsiteUrlAccessors<FundAdminProvisioningForm> = {
  getClientCode: (form) => form.clientCode,
  getWebsiteUrl: (form) => form.websiteUrl,
  setClientCode: (form, clientCode) => ({ ...form, clientCode }),
};

const FundAdminProvisioning = () => {
  const [form, setForm] = useState<FundAdminProvisioningForm>(getInitialFundAdminProvisioningForm);
  const [clientCodeExists, setClientCodeExists] = useState(false);
  const { executionState, setExecutionState } = useExecutionState();

  const prefillClientCodeFromUrl = useClientCodeFromWebsiteUrl(form, setForm, clientCodeAccessors);

  const [organizations, fetchOrganizationsError, { fetch: refetchOrganizations }] = useFetch(
    api.clientProvisioning.getExistingOrganizations
  );

  const handleJobFinished = useCallback(
    (jobProgress: JobProgress) => {
      if (jobProgress.properties["Cancelled"] === "true") {
        setExecutionState({
          status: "warning",
          text: "Provisioning was cancelled.",
          details: ["The client may be partially created. Deprovision it before retrying."],
        });
        refetchOrganizations();
        return;
      }

      if (jobProgress.isSuccess) {
        const clientCode = jobProgress.properties["CreatedClientCode"];
        setExecutionState({
          status: "success",
          details: ["Client provisioning completed successfully."],
          links: clientCode
            ? [{ label: "Open in Admin Portal", href: `${getVariables().ADMIN_PORTAL_URI}/${clientCode}/home` }]
            : undefined,
        });
        refetchOrganizations();
      } else {
        setExecutionState({
          status: "error",
          errorMessage: jobProgress.errorMessage,
        });
      }
    },
    [setExecutionState, refetchOrganizations]
  );

  const { jobProgress, startJobProgressCheck } = useJobProgress(api.clientProvisioning.getJobProgress, {
    onJobFinished: handleJobFinished,
  });

  const [sherwebOrganizations, fetchSherwebOrganizationsError] = useFetch(
    api.clientProvisioning.getSherWebOrganizations
  );

  if (fetchOrganizationsError) {
    return <LoadingFailedAlert apiError={fetchOrganizationsError} />;
  }

  if (fetchSherwebOrganizationsError) {
    return <LoadingFailedAlert apiError={fetchSherwebOrganizationsError} />;
  }

  if (organizations === undefined || sherwebOrganizations === undefined) {
    return <InlineLoader />;
  }

  const handleProvision = async () => {
    const validationResults = validateFundAdminProvisioningForm(form);
    setForm((prev) => ({ ...prev, validationResults }));
    if (!isFormValid(validationResults)) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const payload = mapFundAdminProvisioningFormToRequest(form);
    const [resp, error] = await provisionFundAdminClient(payload);
    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    startJobProgressCheck(resp.operationId);
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle
        title="Provision Fund Administrator Client"
        subtitle="📋 What gets provisioned"
        defaultExpanded={false}
      >
        🏢 Create a new Fund Administrator organization with complete infrastructure setup
        <br />
        <br />
        • Client record as organization (parent entity)
        <br />
        • Business Central tenant connection
        <br />
        • Azure Blob Storage containers
        <br />
        • API Gateway application
        <br />
        • Database schemas and permissions
        <br />
        • Cosmos DB database
        <br />
        • Default Data Model Configuration
        <br />
        <br />
        <strong>Note:</strong> The provisioning does not require a BC company to be created beforehand - only the BC
        tenant.
        <br />
        <br />
        ⏱️ <strong>Duration:</strong> Full provisioning process takes a few seconds
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="Title"
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          error={form.validationResults.title?.isValid === false}
          helperText={form.validationResults.title?.error}
        />
        <FieldHelp>Display name of the Fund Administrator organization (e.g., &apos;ABC Fund Services&apos;)</FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="Website URL"
          value={form.websiteUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, websiteUrl: e.target.value }))}
          onBlur={prefillClientCodeFromUrl}
          error={form.validationResults.websiteUrl?.isValid === false}
          helperText={form.validationResults.websiteUrl?.error}
        />
        <FieldHelp>
          Public website URL of the Fund Administrator organization.
          <br />
          <br />
          The website&apos;s favicon will be automatically extracted and used as the client&apos;s icon throughout the
          system.
          <br />
          <br />
          Optional, but recommended for better visual identification.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="Code"
          value={form.clientCode}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, clientCode: e.target.value }));
            setClientCodeExists(false);
          }}
          onBlur={() => {
            if (form.clientCode.trim() && organizations) {
              setClientCodeExists(doesClientCodeExist(form.clientCode, organizations));
            }
          }}
          error={form.validationResults.clientCode?.isValid === false}
          helperText={getClientCodeHelperText(
            form.validationResults.clientCode?.error,
            clientCodeExists,
            form.continueIfClientAlreadyExists
          )}
          sx={
            clientCodeExists
              ? {
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "warning.main",
                      borderWidth: 2,
                    },
                  },
                }
              : undefined
          }
        />
        <FieldHelp>
          <strong>Naming Convention (use organization domain):</strong>
          <br />• <strong>Classical fund administrator</strong> (managing multiple clients): Use domain name without
          extension
          <br />
          &nbsp;&nbsp;Example: &quot;gpfs.com&quot; → <strong>gpfs</strong>
          <br />• <strong>Fund manager</strong> (1-2 portfolio companies): Add &quot;org&quot; suffix to domain name
          <br />
          &nbsp;&nbsp;Example: &quot;newvest.com&quot; → <strong>newvestorg</strong>
          <br />
          <br />
          <strong>Validation Rules:</strong>
          <br />
          • 3-30 characters
          <br />
          • Only lowercase letters and digits
          <br />
          • No special characters (no dots, dashes, underscores)
          <br />• Must be unique across all clients
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <FormControl fullWidth error={form.validationResults.location?.isValid === false}>
          <FormLabel>Location</FormLabel>
          <RadioGroup
            row
            value={form.location}
            onChange={(_, value) => value && setForm((prev) => ({ ...prev, location: value as ClientLocation }))}
          >
            <FormControlLabel
              value="US"
              control={<Radio />}
              label={
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <CountryFlag countryCodeAlpha2="us" /> <Typography>US</Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value="EU"
              control={<Radio />}
              label={
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <CountryFlag countryCodeAlpha2="eu" /> <Typography>EU</Typography>
                </Stack>
              }
            />
          </RadioGroup>
          {form.validationResults.location?.error && (
            <FormHelperText error>{form.validationResults.location?.error}</FormHelperText>
          )}
        </FormControl>
        <FieldHelp>
          <strong>⚠️ IMPORTANT:</strong> Location MUST match the geographic location of the Business Central tenant
          being provisioned.
          <br />
          <br />
          <strong>US:</strong> Data stored in Azure East US 2 region
          <br />
          <strong>EU:</strong> Data stored in Azure Netherlands region
          <br />
          <br />
          Location determines data residency, compliance, and which Azure resources are used.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Organization Domains (one per line)"
          value={form.organizationDomainsMemo}
          onChange={(e) => setForm((prev) => ({ ...prev, organizationDomainsMemo: e.target.value }))}
          error={form.validationResults.organizationDomainsMemo?.isValid === false}
          helperText={form.validationResults.organizationDomainsMemo?.error}
        />
        <FieldHelp>
          Email domains used by this organization (e.g., &quot;abcfund.com&quot;).
          <br />
          <br />
          Enter one domain per line. These domains determine whether users are <strong>internal</strong> (matching
          domain) or <strong>external</strong> (different domain) to the organization.
          <br />
          <br />
          Used for user identification, access control, and permissions.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <Autocomplete
          fullWidth
          options={sherwebOrganizations}
          value={form.sherwebOrganization}
          onChange={(_, value) => setForm((prev) => ({ ...prev, sherwebOrganization: value }))}
          getOptionLabel={(option) => option.displayName}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sherweb Organization"
              error={form.validationResults.sherwebOrganization?.isValid === false}
              helperText={form.validationResults.sherwebOrganization?.error}
            />
          )}
        />
        <FieldHelp>
          Sherweb is the service where we provision BC tenants and manage BC licences.
          <br />
          <br />
          Select the pre-created organization for this client from the list.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="Primary BC Tenant ID"
          value={form.primaryBcTenantId}
          onChange={(e) => setForm((prev) => ({ ...prev, primaryBcTenantId: e.target.value }))}
          error={form.validationResults.primaryBcTenantId?.isValid === false}
          helperText={form.validationResults.primaryBcTenantId?.error}
        />
        <FieldHelp>
          The ID (GUID) of Business Central tenant.
          <br />
          <br />
          <strong>How to get it:</strong> From Business Central URL, extract the GUID after the domain:
          <br />
          <code style={{ fontSize: "0.75rem" }}>
            https://businesscentral.dynamics.com/
            <strong style={{ color: orange[600] }}>ea69728d-da50-4295-b231-c0682ed2d87f</strong>/Demo
          </code>
          <br />
          In this example, the Tenant ID is: <strong>ea69728d-da50-4295-b231-c0682ed2d87f</strong>
          <br />
          <br />
          This connects Entrilia to the fund administrator&apos;s Business Central accounting system.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="Primary BC Environment"
          value={form.primaryBcEnvironment}
          onChange={(e) => setForm((prev) => ({ ...prev, primaryBcEnvironment: e.target.value }))}
        />
        <FieldHelp>
          Business Central environment name (e.g., &quot;Production&quot;, &quot;Sandbox&quot;).
          <br />
          <br />
          Defaults to &quot;Production&quot; if not specified.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <FormControlLabel
          label="Continue if already exists"
          control={
            <Checkbox
              checked={form.continueIfClientAlreadyExists}
              onChange={(_, checked) => setForm((prev) => ({ ...prev, continueIfClientAlreadyExists: checked }))}
            />
          }
        />
        <FieldHelp>
          <strong>⚠️ IMPORTANT:</strong> Always start with this option <strong>DISABLED</strong> to prevent overwriting
          existing clients.
          <br />
          <br />
          <strong>Only enable if:</strong>
          <br />
          • Initial provisioning attempt failed
          <br />• You are re-provisioning after fixing an issue
        </FieldHelp>
      </Stack>
      <Divider flexItem />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleProvision}
          loading={executionState.status === "in-progress"}
        >
          Provision
        </Button>
        <Button
          variant="text"
          color="secondary"
          onClick={() => setForm(getInitialFundAdminProvisioningForm())}
          disabled={executionState.status === "in-progress"}
        >
          Reset
        </Button>
      </Stack>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
      {jobProgress && <AsyncJobProgress title="Please wait..." jobProgress={jobProgress} />}
    </Stack>
  );
};

export default FundAdminProvisioning;
